import { useAdminAuthStore } from "@/features/auth/store/admin-auth.store";
import { hasPermission } from "@/lib/permissions";

/**
 * A route an operator needs a specific permission to see.
 *
 * PRESENTATION, NOT ENFORCEMENT. The server refuses either way -- every admin
 * route runs `requirePermission` and answers 403 regardless of what the browser
 * renders. What this changes is what the operator is TOLD.
 *
 * Without it, an operator lacking the grant reached the screen, every request
 * 403'd, and the page's generic "Could not load products. Try again" offered a
 * retry that could never succeed. A permission problem and a server outage
 * looked identical, and only one of them is worth waiting out.
 *
 * `canSeeNav` already hides the nav entry; this covers arrival by URL, bookmark,
 * or a link someone pasted into chat.
 */
export function RequirePermission({
	permission,
	anyOf,
	children,
}: {
	/** The one permission the screen needs. */
	permission?: string;
	/** Or: any one of these (a screen two roles reach by different grants). */
	anyOf?: readonly string[];
	children: React.ReactNode;
}) {
	const operator = useAdminAuthStore((state) => state.operator);
	const needed = anyOf ?? (permission ? [permission] : []);

	if (!needed.some((one) => hasPermission(operator, one))) {
		return (
			<div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
				<p className="font-semibold">You do not have access to this screen.</p>
				<p className="mt-1">
					{needed.length === 1 ? (
						<>
							It needs the <code>{needed[0]}</code> permission.
						</>
					) : (
						<>
							It needs one of these permissions:{" "}
							{needed.map((one, index) => (
								<span key={one}>
									{index > 0 ? ", " : ""}
									<code>{one}</code>
								</span>
							))}
							.
						</>
					)}{" "}
					An administrator can grant it from the roles screen.
				</p>
			</div>
		);
	}

	return <>{children}</>;
}
